import { Link } from 'react-router'
import { RiArrowRightLine, RiCheckLine } from 'react-icons/ri'
import { ROUTES } from '@/constants/routes'
import { useInView } from '@/hooks'

const trust = [
  'No commitment — just a conversation',
  'Completely private and confidential',
  'Serving Essex, North Kent & East London',
]

export const CTASection = () => {
  const { ref, inView } = useInView()

  return (
    <section className="relative bg-base-50 py-20 lg:py-28 overflow-hidden">
      {/* Decorative circles */}
      <div
        aria-hidden="true"
        className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-crimson-50 opacity-60 -translate-y-1/2 translate-x-1/3 pointer-events-none"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-gold-50 opacity-60 translate-y-1/2 -translate-x-1/4 pointer-events-none"
      />

      <div ref={ref} className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row items-center gap-14 lg:gap-20">
          {/* ── Left: emotional statement ── */}
          <div className="flex-1 text-center lg:text-left space-y-6">
            <div className={inView ? 'animate-fade-in-up' : 'opacity-0'}>
              <h2
                className="font-display font-bold text-base-900 leading-[1.08]"
                style={{ fontSize: 'clamp(2.25rem, 5vw, 3.75rem)' }}
              >
                Ready to <em className="text-crimson-600 not-italic italic block">Learn More?</em>
              </h2>
            </div>

            <div
              className={`w-12 h-[3px] rounded-full bg-gradient-to-r from-crimson-600 to-gold-500 mx-auto lg:mx-0 ${inView ? 'animate-fade-in' : 'opacity-0'}`}
              style={{ animationDelay: inView ? '0.15s' : undefined }}
            />

            <p
              className={`text-base-600 text-[16px] leading-[1.85] max-w-md mx-auto lg:mx-0 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: inView ? '0.2s' : undefined }}
            >
              A free, no-obligation consultation call is the perfect place to start. Ask questions,
              share your hopes and concerns, and discover whether we're the right fit for one
              another. I'd be honoured to support you on your journey.
            </p>

            <ul
              className={`space-y-3 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: inView ? '0.3s' : undefined }}
            >
              {trust.map((item) => (
                <li key={item} className="flex items-center gap-3 justify-center lg:justify-start">
                  <span className="w-5 h-5 rounded-full bg-crimson-50 flex items-center justify-center shrink-0">
                    <RiCheckLine size={11} className="text-crimson-600" />
                  </span>
                  <span className="text-[13.5px] text-base-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Right: action card ── */}
          <div
            className={`w-full lg:w-[380px] shrink-0 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: inView ? '0.2s' : undefined }}
          >
            <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-[0_12px_40px_-4px_rgba(155,28,28,0.12),0_4px_12px_-4px_rgba(0,0,0,0.06)] border border-base-100">
              <div className="w-10 h-[3px] rounded-full bg-gradient-to-r from-gold-500 to-gold-300 mb-6" />

              <h3
                className="font-display font-bold text-base-900 leading-tight mb-3"
                style={{ fontSize: 'clamp(1.35rem, 2.5vw, 1.75rem)' }}
              >
                Book Your Free <em className="text-crimson-600 not-italic italic">Support Call</em>
              </h3>

              <p className="text-base-500 text-[14px] leading-relaxed mb-8">
                Begin your journey by sharing a few details. Simone will be in touch to walk
                alongside you every step of the way.
              </p>

              <Link
                to={ROUTES.ENQUIRY}
                className="btn-primary w-full justify-center text-[0.9375rem] py-4 mb-4"
              >
                Unlock Your Free Call
                <RiArrowRightLine size={18} />
              </Link>

              <p className="text-[11px] text-base-400 text-center">
                Takes less than 2 minutes &middot; No obligation
              </p>

              <div className="my-6 h-px bg-base-100" />

              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: '15+', label: 'Years Advocacy Experience' },
                  { value: '100%', label: 'Confidential' },
                ].map(({ value, label }) => (
                  <div key={label} className="text-center">
                    <p className="font-display font-bold text-gold-500 text-2xl leading-none mb-1">
                      {value}
                    </p>
                    <p className="text-[11px] text-base-400 uppercase tracking-wide">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
